import React, { useState, useEffect, useRef } from "react";
import { 
    Card, Row, Col, Button, Badge, Table, Spinner, Alert, 
    Modal, Form, InputGroup, Tooltip, OverlayTrigger 
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate, formatNumber } from "../utils/formatters";
import { STATUT_FACTURE, MODE_REGLEMENT } from "../utils/constantes";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";
import logo from "../assets/img/IMG-20250130-WA0008.jpg";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 3000 }) => {
    if (!show) return null;

    const toastConfig = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    };
    const config = toastConfig[type] || toastConfig.info;

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom">
                    <p className="mb-0">{message}</p>
                </div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function DetailFacture() {
    const navigate = useNavigate();
    const { id } = useParams();
    const printRef = useRef();

    const [facture, setFacture] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showPaiementModal, setShowPaiementModal] = useState(false);
    const [showAvoirModal, setShowAvoirModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [generatingPDF, setGeneratingPDF] = useState(false);
    const [societeInfos, setSocieteInfos] = useState(null);
    const [logoBase64, setLogoBase64] = useState('');
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });

    const [taxes, setTaxes] = useState({
        tva_taux: 18,
        css_taux: 1,
        tva_active: true,
        css_active: true
    });

    const [paiement, setPaiement] = useState({
        montant: '',
        mode_reglement: 'especes',
        reference: '',
        date_reglement: new Date().toISOString().split('T')[0]
    });

    const [avoir, setAvoir] = useState({
        motif: '',
        lignes: []
    });

    useEffect(() => {
        fetchFacture();
        fetchTaxes();
        fetchSocieteInfos();
        loadLogoBase64();
    }, [id]);

    /* ============================================================
       FONCTIONS MÉTIER (inchangées)
       ============================================================ */
    const fetchSocieteInfos = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=societe`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) setSocieteInfos(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement société", err);
        }
    };

    const loadLogoBase64 = () => {
        return new Promise((resolve) => {
            try {
                const img = new Image();
                img.crossOrigin = "Anonymous";
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    const dataURL = canvas.toDataURL('image/jpeg');
                    setLogoBase64(dataURL);
                    resolve(dataURL);
                };
                img.onerror = () => { setLogoBase64(''); resolve(''); };
                img.src = logo;
            } catch (error) {
                setLogoBase64('');
                resolve('');
            }
        });
    };

    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) setTaxes(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };

    const fetchFacture = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setFacture(result.data);
            if (result.data?.lignes) {
                setAvoir({
                    ...avoir,
                    lignes: result.data.lignes.map(l => ({
                        ...l,
                        selected: true,
                        quantite_avoir: l.quantite
                    }))
                });
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const showToast = (type, title, message) => {
        setToast({ show: true, type, title, message });
    };
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });

    const handlePaiementSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            const id_utilisateur = user?.id || user?.id_utilisateur;
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/factures.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'encaisser',
                    id_facture: id,
                    montant: parseFloat(paiement.montant),
                    mode_reglement: paiement.mode_reglement,
                    reference: paiement.reference,
                    id_utilisateur: id_utilisateur
                })
            });
            const result = await response.json();
            if (result.success) {
                setShowPaiementModal(false);
                fetchFacture();
                showToast('success', 'Paiement enregistré', 'Le paiement a été enregistré avec succès');
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors du paiement");
            }
        } catch (err) {
            showToast('error', 'Erreur de connexion', "Impossible de contacter le serveur");
        } finally {
            setSaving(false);
        }
    };

    const handleAvoirSubmit = async (e) => {
        e.preventDefault();
        const lignesSelectionnees = avoir.lignes.filter(l => l.selected);
        if (lignesSelectionnees.length === 0) {
            showToast('error', 'Sélection requise', "Sélectionnez au moins une ligne pour l'avoir");
            return;
        }
        setSaving(true);
        try {
            const userStr = localStorage.getItem("user");
            const user = JSON.parse(userStr);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/factures.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'avoir',
                    id_facture: id,
                    motif: avoir.motif,
                    lignes: lignesSelectionnees.map(l => ({
                        id_produit: l.id_produit,
                        quantite: l.quantite_avoir,
                        prix_unitaire_ht: l.prix_unitaire_ht,
                        tva_taux: l.tva_taux,
                        css_taux: l.css_taux
                    })),
                    id_utilisateur: user.id || user.id_utilisateur
                })
            });
            const result = await response.json();
            if (result.success) {
                setShowAvoirModal(false);
                fetchFacture();
                showToast('success', 'Avoir créé', "L'avoir a été créé avec succès");
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la création de l'avoir");
            }
        } catch (err) {
            showToast('error', 'Erreur de connexion', "Impossible de contacter le serveur");
        } finally {
            setSaving(false);
        }
    };

    const company = {
        name: societeInfos?.nom || 'GEOTECH',
        address: societeInfos?.adresse || 'Libreville, Gabon',
        phone: societeInfos?.telephone || '+241 00 00 00 00',
        email: societeInfos?.email || 'contact@geotech.com',
        site_web: societeInfos?.site_web || 'www.geotech.com',
        rc: societeInfos?.rc || 'RC123456',
        nif: societeInfos?.nif || 'NIF123456',
        patente: societeInfos?.patente || 'PAT123456'
    };

    /* ============================================================
       IMPRESSION / PDF (logique conservée)
       ============================================================ */
  const genererHTMLImpression = () => {
    if (!facture) return '';

    const statutConfig = {
        brouillon:       { class: 'draft',    label: 'BROUILLON' },
        envoye:          { class: 'sent',     label: 'ENVOYÉE' },
        accepte:         { class: 'accepted', label: 'ACCEPTÉE' },
        refuse:          { class: 'refused',  label: 'REFUSÉE' },
        payee:           { class: 'paid',     label: 'PAYÉE' },
        impayee:         { class: 'unpaid',   label: 'IMPAYÉE' },
        payee_partielle: { class: 'partial',  label: 'PAIEMENT PARTIEL' },
        annulee:         { class: 'cancelled',label: 'ANNULÉE' }
    };

    const statut = statutConfig[facture.statut] || statutConfig.brouillon;
    const totalHT = facture.montant_ht || 0;
    const totalTVA = facture.montant_tva || 0;
    const totalCSS = facture.montant_css || 0;
    const totalTTC = facture.net_a_payer || 0;
    const montantRegle = facture.montant_regle || 0;
    const reste = totalTTC - montantRegle;
    const logoSrc = logoBase64;
    const logoText = company.name.substring(0, 2).toUpperCase();

    // Montant en lettres
    const nombreEnLettres = (n) => {
        const unites = ['zéro','un','deux','trois','quatre','cinq','six','sept','huit','neuf','dix','onze','douze','treize','quatorze','quinze','seize','dix-sept','dix-huit','dix-neuf'];
        const dizaines = ['','','vingt','trente','quarante','cinquante','soixante','soixante-dix','quatre-vingt','quatre-vingt-dix'];
        if (n < 20) return unites[n];
        if (n < 100) {
            const d = Math.floor(n / 10);
            const u = n % 10;
            return dizaines[d] + (u === 1 && d !== 8 ? '-et-un' : u ? '-' + unites[u] : '');
        }
        if (n < 1000) {
            const c = Math.floor(n / 100);
            const r = n % 100;
            return (c === 1 ? 'cent' : unites[c] + ' cent') + (r ? ' ' + nombreEnLettres(r) : '');
        }
        if (n < 1000000) {
            const m = Math.floor(n / 1000);
            const r = n % 1000;
            return (m === 1 ? 'mille' : nombreEnLettres(m) + ' mille') + (r ? ' ' + nombreEnLettres(r) : '');
        }
        const mi = Math.floor(n / 1000000);
        const r = n % 1000000;
        return (mi === 1 ? 'un million' : nombreEnLettres(mi) + ' millions') + (r ? ' ' + nombreEnLettres(r) : '');
    };
    const montantEnLettres = nombreEnLettres(Math.round(totalTTC)).toUpperCase();

    return `
        <!DOCTYPE html>
        <html lang="fr">
        <head>
            <meta charset="UTF-8">
            <title>Facture ${facture.numero_facture}</title>
            <style>
                @page {
                    size: A4;
                    margin: 12mm 10mm;
                }

                * { margin: 0; padding: 0; box-sizing: border-box; }

                body {
                    font-family: 'Helvetica Neue', 'Segoe UI', Arial, sans-serif;
                    background: white;
                    color: #1e293b;
                    font-size: 10.5px;
                    line-height: 1.5;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }

                .invoice {
                    max-width: 210mm;
                    margin: 0 auto;
                    background: white;
                    position: relative;
                }

                /* ============================================
                   BANDEAU SUPÉRIEUR AVEC DÉGRADÉ
                   ============================================ */
                .top-band {
                    display: flex;
                    justify-content: space-between;
                    align-items: stretch;
                    border-radius: 8px 8px 0 0;
                    overflow: hidden;
                    background: linear-gradient(135deg, #1a3c5e 0%, #295AA7 100%);
                    color: white;
                    padding: 0;
                }

                .top-band-left {
                    padding: 22px 26px;
                    display: flex;
                    align-items: center;
                    gap: 18px;
                    flex: 1;
                }

                .logo-container {
                    width: 70px;
                    height: 70px;
                    background: white;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                    flex-shrink: 0;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }
                .logo-container img {
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                }
                .logo-fallback {
                    font-size: 24px;
                    font-weight: 800;
                    color: #1a3c5e;
                    letter-spacing: 1px;
                }

                .company-header h1 {
                    font-size: 22px;
                    font-weight: 800;
                    letter-spacing: 2px;
                    margin-bottom: 4px;
                    text-transform: uppercase;
                }
                .company-header .tagline {
                    font-size: 9px;
                    letter-spacing: 3px;
                    opacity: 0.75;
                    text-transform: uppercase;
                    font-weight: 300;
                }

                .top-band-right {
                    padding: 22px 26px;
                    text-align: right;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    min-width: 200px;
                    background: #C42625;
                    border-left: 1px solid rgba(255,255,255,0.15);
                }
                .doc-type {
                    font-size: 26px;
                    font-weight: 800;
                    letter-spacing: 4px;
                    margin-bottom: 6px;
                    text-transform: uppercase;
                }
                .doc-number {
                    font-size: 11px;
                    font-family: 'Courier New', monospace;
                    font-weight: 600;
                    letter-spacing: 1px;
                    margin-bottom: 8px;
                    opacity: 0.95;
                }
                .doc-date {
                    font-size: 9px;
                    opacity: 0.75;
                    letter-spacing: 1px;
                }

                /* ============================================
                   BANDEAU DE STATUT
                   ============================================ */
                .status-strip {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 8px 20px;
                    font-size: 10px;
                    font-weight: 700;
                    letter-spacing: 3px;
                    text-transform: uppercase;
                    border-bottom: 3px solid;
                }
                .status-paid     { background: #ecfdf5; color: #065f46; border-color: #10b981; }
                .status-unpaid   { background: #fef2f2; color: #991b1b; border-color: #ef4444; }
                .status-partial  { background: #fffbeb; color: #92400e; border-color: #f59e0b; }
                .status-draft    { background: #f1f5f9; color: #475569; border-color: #94a3b8; }
                .status-accepted { background: #eff6ff; color: #1e40af; border-color: #3b82f6; }
                .status-refused  { background: #fef2f2; color: #991b1b; border-color: #ef4444; }
                .status-cancelled{ background: #f1f5f9; color: #475569; border-color: #64748b; }

                /* ============================================
                   BLOC PARTIES (ÉMETTEUR / CLIENT)
                   ============================================ */
                .parties {
                    display: flex;
                    gap: 14px;
                    padding: 20px 0;
                    border-bottom: 1px solid #e2e8f0;
                }
                .party {
                    flex: 1;
                    padding: 14px 16px;
                    background: #f8fafc;
                    border-radius: 8px;
                    border-left: 3px solid #295AA7;
                }
                .party-client {
                    border-left-color: #21ACCD;
                }
                .party-label {
                    font-size: 8.5px;
                    font-weight: 700;
                    letter-spacing: 2px;
                    text-transform: uppercase;
                    color: #295AA7;
                    margin-bottom: 8px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .party-client .party-label {
                    color: #21ACCD;
                }
                .party-label::before {
                    content: '';
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: currentColor;
                }
                .party-name {
                    font-size: 13px;
                    font-weight: 700;
                    color: #0f172a;
                    margin-bottom: 6px;
                }
                .party-details {
                    font-size: 9.5px;
                    color: #475569;
                    line-height: 1.7;
                }
                .party-details .label {
                    display: inline-block;
                    min-width: 60px;
                    color: #94a3b8;
                    font-weight: 500;
                }

                /* ============================================
                   MÉTADONNÉES FACTURE (DATES)
                   ============================================ */
                .invoice-meta {
                    display: flex;
                    justify-content: flex-end;
                    gap: 10px;
                    padding: 14px 0;
                    margin-bottom: 8px;
                }
                .meta-card {
                    min-width: 140px;
                    padding: 8px 14px;
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 6px;
                    text-align: center;
                }
                .meta-card .meta-label {
                    font-size: 8px;
                    font-weight: 600;
                    letter-spacing: 1.5px;
                    text-transform: uppercase;
                    color: #94a3b8;
                    margin-bottom: 3px;
                }
                .meta-card .meta-value {
                    font-size: 11px;
                    font-weight: 700;
                    color: #1e293b;
                }
                .meta-card.urgent .meta-value {
                    color: #ef4444;
                }

                /* ============================================
                   TABLEAU DES LIGNES
                   ============================================ */
                .table-wrapper {
                    border-radius: 8px;
                    overflow: hidden;
                    border: 1px solid #e2e8f0;
                    margin-bottom: 16px;
                }
                table.lines {
                    width: 100%;
                    border-collapse: collapse;
                }
                table.lines thead {
                    background: linear-gradient(135deg, #1a3c5e, #295AA7);
                    color: white;
                }
                table.lines thead th {
                    padding: 10px 12px;
                    font-size: 9px;
                    font-weight: 700;
                    letter-spacing: 1.2px;
                    text-transform: uppercase;
                    text-align: left;
                }
                table.lines thead th.center { text-align: center; }
                table.lines thead th.right  { text-align: right; }

                table.lines tbody td {
                    padding: 10px 12px;
                    font-size: 10px;
                    color: #334155;
                    border-bottom: 1px solid #f1f5f9;
                    vertical-align: top;
                }
                table.lines tbody tr:nth-child(even) {
                    background: #fafbfc;
                }
                table.lines tbody tr:last-child td {
                    border-bottom: none;
                }
                table.lines td.center { text-align: center; }
                table.lines td.right  { text-align: right; }
                table.lines td.ref {
                    font-family: 'Courier New', monospace;
                    font-size: 9px;
                    color: #64748b;
                }
                table.lines td.designation {
                    font-weight: 600;
                    color: #0f172a;
                }
                table.lines td.total {
                    font-weight: 700;
                    color: #1a3c5e;
                }
                .remise-tag {
                    display: inline-block;
                    padding: 1px 6px;
                    background: #fef3c7;
                    color: #92400e;
                    border-radius: 3px;
                    font-size: 8.5px;
                    font-weight: 600;
                }

                /* ============================================
                   TOTAUX
                   ============================================ */
                .totals-section {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 20px;
                    margin-bottom: 20px;
                }

                .amount-words {
                    flex: 1;
                    padding: 12px 16px;
                    background: #f8fafc;
                    border-radius: 8px;
                    border: 1px dashed #cbd5e1;
                }
                .amount-words .aw-label {
                    font-size: 8px;
                    font-weight: 700;
                    letter-spacing: 1.5px;
                    text-transform: uppercase;
                    color: #64748b;
                    margin-bottom: 6px;
                }
                .amount-words .aw-value {
                    font-size: 10px;
                    font-style: italic;
                    font-weight: 600;
                    color: #1a3c5e;
                    line-height: 1.5;
                    text-transform: capitalize;
                }

                .totals-box {
                    width: 320px;
                    flex-shrink: 0;
                    border-radius: 8px;
                    overflow: hidden;
                    border: 1px solid #e2e8f0;
                }
                .totals-box .row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 8px 14px;
                    font-size: 10px;
                    border-bottom: 1px solid #f1f5f9;
                }
                .totals-box .row:last-child {
                    border-bottom: none;
                }
                .totals-box .row .lbl {
                    color: #64748b;
                }
                .totals-box .row .val {
                    font-weight: 600;
                    color: #334155;
                }

                .totals-box .row.total-ttc {
                    background: linear-gradient(135deg, #1a3c5e, #295AA7);
                    color: white;
                    padding: 12px 14px;
                }
                .totals-box .row.total-ttc .lbl {
                    color: white;
                    font-weight: 700;
                    letter-spacing: 1.5px;
                    font-size: 10px;
                    text-transform: uppercase;
                }
                .totals-box .row.total-ttc .val {
                    color: white;
                    font-size: 15px;
                    font-weight: 800;
                }

                .totals-box .row.paid .val { color: #10b981; }
                .totals-box .row.remaining {
                    background: #fef2f2;
                    padding: 12px 14px;
                }
                .totals-box .row.remaining .lbl {
                    color: #991b1b;
                    font-weight: 700;
                    letter-spacing: 0.5px;
                    text-transform: uppercase;
                    font-size: 9.5px;
                }
                .totals-box .row.remaining .val {
                    color: #dc2626;
                    font-weight: 800;
                    font-size: 15px;
                }

                /* ============================================
                   PIED DE PAGE — SIGNATURES
                   ============================================ */
                .signatures {
                    display: flex;
                    justify-content: space-between;
                    gap: 20px;
                    margin-top: 24px;
                    padding-top: 18px;
                    border-top: 1px solid #e2e8f0;
                }
                .signature-box {
                    flex: 1;
                    text-align: center;
                    padding-top: 8px;
                }
                .signature-box .sig-title {
                    font-size: 9px;
                    font-weight: 700;
                    letter-spacing: 1.5px;
                    text-transform: uppercase;
                    color: #64748b;
                    margin-bottom: 4px;
                }
                .signature-box .sig-hint {
                    font-size: 8px;
                    font-style: italic;
                    color: #94a3b8;
                    margin-bottom: 20px;
                }
                .signature-box .sig-area {
                    height: 60px;
                    border-bottom: 1.5px solid #cbd5e1;
                    margin-bottom: 6px;
                }
                .signature-box .sig-area.stamp {
                    border-bottom: 1.5px dashed #cbd5e1;
                }

                /* ============================================
                   FOOTER LÉGAL
                   ============================================ */
                .legal-footer {
                    margin-top: 22px;
                    padding-top: 14px;
                    border-top: 2px solid #1a3c5e;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 8px;
                    color: #94a3b8;
                    letter-spacing: 0.5px;
                }
                .legal-footer .legal-left {
                    display: flex;
                    gap: 16px;
                    flex-wrap: wrap;
                }
                .legal-footer .legal-right {
                    text-align: right;
                    font-weight: 600;
                    color: #64748b;
                }
                .legal-footer strong {
                    color: #1a3c5e;
                }

                /* ============================================
                   WATERMARK (FACTURE PAYÉE / ANNULÉE)
                   ============================================ */
                .watermark {
                    position: fixed;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%) rotate(-35deg);
                    font-size: 100px;
                    font-weight: 900;
                    letter-spacing: 8px;
                    opacity: 0.05;
                    pointer-events: none;
                    z-index: 0;
                    white-space: nowrap;
                    text-transform: uppercase;
                }
                .watermark.paid    { color: #10b981; }
                .watermark.unpaid  { color: #ef4444; }
                .watermark.cancelled { color: #64748b; }

                /* ============================================
                   IMPRESSION
                   ============================================ */
                @media print {
                    body { background: white; padding: 0; }
                    .watermark { display: block; }
                    .no-print { display: none; }
                }
            </style>
        </head>
        <body>

            ${facture.statut === 'payee' ? '<div class="watermark paid">PAYÉE</div>' : ''}
            ${facture.statut === 'annulee' ? '<div class="watermark cancelled">ANNULÉE</div>' : ''}

            <div class="invoice">

                <!-- BANDEAU SUPÉRIEUR -->
                <div class="top-band">
                    <div class="top-band-left">
                        <div class="logo-container">
                            ${logoSrc ? `<img src="${logoSrc}" alt="Logo" />` : `<div class="logo-fallback">${logoText}</div>`}
                        </div>
                        <div class="company-header">
                            <h1>${company.name}</h1>
                            <div class="tagline">Excellence & Fiabilité</div>
                        </div>
                    </div>
                    <div class="top-band-right">
                        <div class="doc-type">Facture</div>
                        <div class="doc-number">N° ${facture.numero_facture}</div>
                        <div class="doc-date">Émise le ${formatDate(facture.date_facture)}</div>
                    </div>
                </div>

                <!-- BANDEAU STATUT -->
                <div class="status-strip status-${statut.class}">
                    ${statut.label}
                </div>

                <!-- PARTIES -->
                <div class="parties">
                    <div class="party">
                        <div class="party-label">Émetteur</div>
                        <div class="party-name">${company.name}</div>
                        <div class="party-details">
                            <div><span class="label">Adresse</span>${company.address}</div>
                            <div><span class="label">Téléphone</span>${company.phone}</div>
                            <div><span class="label">Email</span>${company.email}</div>
                            <div><span class="label">Site web</span>${company.site_web}</div>
                        </div>
                    </div>
                    <div class="party party-client">
                        <div class="party-label">Client</div>
                        <div class="party-name">${facture.client_nom || 'Client non spécifié'}</div>
                        <div class="party-details">
                            ${facture.code_client ? `<div><span class="label">Code</span>${facture.code_client}</div>` : ''}
                            ${facture.client_telephone ? `<div><span class="label">Téléphone</span>${facture.client_telephone}</div>` : ''}
                            ${facture.client_email ? `<div><span class="label">Email</span>${facture.client_email}</div>` : ''}
                            ${facture.client_adresse ? `<div><span class="label">Adresse</span>${facture.client_adresse}</div>` : ''}
                        </div>
                    </div>
                </div>

                <!-- MÉTADONNÉES -->
                <div class="invoice-meta">
                    <div class="meta-card">
                        <div class="meta-label">Date d'émission</div>
                        <div class="meta-value">${formatDate(facture.date_facture)}</div>
                    </div>
                    <div class="meta-card ${new Date(facture.date_echeance) < new Date() && reste > 0 ? 'urgent' : ''}">
                        <div class="meta-label">Date d'échéance</div>
                        <div class="meta-value">${formatDate(facture.date_echeance)}</div>
                    </div>
                </div>

                <!-- TABLEAU DES LIGNES -->
                <div class="table-wrapper">
                    <table class="lines">
                        <thead>
                            <tr>
                                <th style="width: 12%;">Référence</th>
                                <th style="width: 38%;">Désignation</th>
                                <th class="center" style="width: 8%;">Qté</th>
                                <th class="right" style="width: 14%;">Prix Unit. HT</th>
                                <th class="center" style="width: 10%;">Remise</th>
                                <th class="right" style="width: 18%;">Total HT</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${facture.lignes?.map(ligne => `
                                <tr>
                                    <td class="ref">${ligne.reference || '-'}</td>
                                    <td class="designation">
                                        ${ligne.designation || ''}
                                        ${ligne.description ? `<div style="font-size: 8.5px; color: #94a3b8; font-weight: 400; margin-top: 2px;">${ligne.description}</div>` : ''}
                                    </td>
                                    <td class="center">${ligne.quantite || 0}</td>
                                    <td class="right">${formatCurrency(ligne.prix_unitaire_ht || 0)}</td>
                                    <td class="center">
                                        ${ligne.remise ? `<span class="remise-tag">${ligne.remise}%</span>` : '<span style="color: #cbd5e1;">—</span>'}
                                    </td>
                                    <td class="right total">${formatCurrency(ligne.montant_ligne_ht || 0)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>

                <!-- TOTAUX + MONTANT EN LETTRES -->
                <div class="totals-section">
                    <div class="amount-words">
                        <div class="aw-label">Arrêté à la somme de</div>
                        <div class="aw-value">${montantEnLettres.toLowerCase()} francs CFA</div>
                    </div>

                    <div class="totals-box">
                        <div class="row">
                            <span class="lbl">Total HT</span>
                            <span class="val">${formatCurrency(totalHT)}</span>
                        </div>
                        ${facture.remise_globale > 0 ? `
                        <div class="row">
                            <span class="lbl">Remise globale</span>
                            <span class="val" style="color: #10b981;">− ${formatCurrency(facture.remise_globale)}</span>
                        </div>
                        ` : ''}
                        ${taxes.tva_active && totalTVA > 0 ? `
                        <div class="row">
                            <span class="lbl">TVA (${taxes.tva_taux}%)</span>
                            <span class="val">${formatCurrency(totalTVA)}</span>
                        </div>
                        ` : ''}
                        ${taxes.css_active && totalCSS > 0 ? `
                        <div class="row">
                            <span class="lbl">CSS (${taxes.css_taux}%)</span>
                            <span class="val">${formatCurrency(totalCSS)}</span>
                        </div>
                        ` : ''}

                        <div class="row total-ttc">
                            <span class="lbl">Total TTC</span>
                            <span class="val">${formatCurrency(totalTTC)}</span>
                        </div>

                        <div class="row paid">
                            <span class="lbl">Montant réglé</span>
                            <span class="val">${formatCurrency(montantRegle)}</span>
                        </div>

                        <div class="row remaining">
                            <span class="lbl">Reste à payer</span>
                            <span class="val">${formatCurrency(reste)}</span>
                        </div>
                    </div>
                </div>

                <!-- SIGNATURES -->
                <div class="signatures">
                    <div class="signature-box">
                        <div class="sig-title">Signature Client</div>
                        <div class="sig-hint">Précédée de la mention « Lu et approuvé »</div>
                        <div class="sig-area"></div>
                    </div>
                    <div class="signature-box">
                        <div class="sig-title">Cachet & Signature</div>
                        <div class="sig-hint">${company.name}</div>
                        <div class="sig-area stamp"></div>
                    </div>
                </div>

                <!-- FOOTER LÉGAL -->
                <div class="legal-footer">
                    <div class="legal-left">
                        ${company.rc ? `<span><strong>RC</strong> ${company.rc}</span>` : ''}
                        ${company.nif ? `<span><strong>NIF</strong> ${company.nif}</span>` : ''}
                        ${company.patente ? `<span><strong>Patente</strong> ${company.patente}</span>` : ''}
                    </div>
                    <div class="legal-right">
                        Document généré électroniquement — ${company.name}
                    </div>
                </div>

            </div>
        </body>
        </html>
    `;
};

    const handlePrint = () => {
        const htmlContent = genererHTMLImpression();
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        const iframeDoc = iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(htmlContent);
        iframeDoc.close();
        iframe.contentWindow.onload = () => {
            iframe.contentWindow.print();
            setTimeout(() => {
                if (document.body.contains(iframe)) document.body.removeChild(iframe);
            }, 1000);
        };
        showToast('info', 'Impression', "Document envoyé à l'impression");
    };

    const generatePDF = () => {
        setGeneratingPDF(true);
        try {
            const htmlContent = genererHTMLImpression();
            const iframe = document.createElement('iframe');
            iframe.style.position = 'absolute';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = 'none';
            document.body.appendChild(iframe);
            const iframeDoc = iframe.contentWindow.document;
            iframeDoc.open();
            iframeDoc.write(htmlContent);
            iframeDoc.close();
            iframe.contentWindow.onload = () => {
                iframe.contentWindow.print();
                setTimeout(() => {
                    if (document.body.contains(iframe)) document.body.removeChild(iframe);
                }, 1000);
            };
            showToast('success', 'PDF généré', 'Document généré avec succès');
        } catch (error) {
            showToast('error', 'Erreur PDF', error.message);
        } finally {
            setGeneratingPDF(false);
        }
    };

    const getStatutBadge = (statut) => {
        const config = STATUT_FACTURE[statut] || STATUT_FACTURE.brouillon;
        const colorMap = {
            brouillon:      { gradient: 'linear-gradient(135deg, #64748b, #475569)', glow: 'rgba(100,116,139,0.3)' },
            envoye:         { gradient: 'linear-gradient(135deg, #3b82f6, #2563eb)', glow: 'rgba(59,130,246,0.3)' },
            accepte:        { gradient: 'linear-gradient(135deg, #10b981, #059669)', glow: 'rgba(16,185,129,0.3)' },
            refuse:         { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', glow: 'rgba(239,68,68,0.3)' },
            payee:          { gradient: 'linear-gradient(135deg, #10b981, #059669)', glow: 'rgba(16,185,129,0.3)' },
            impayee:        { gradient: 'linear-gradient(135deg, #ef4444, #dc2626)', glow: 'rgba(239,68,68,0.3)' },
            payee_partielle:{ gradient: 'linear-gradient(135deg, #f59e0b, #d97706)', glow: 'rgba(245,158,11,0.3)' },
            annulee:        { gradient: 'linear-gradient(135deg, #dc2626, #b91c1c)', glow: 'rgba(220,38,38,0.3)' }
        };
        const colors = colorMap[statut] || colorMap.brouillon;

        return (
            <span className="status-badge-charte" style={{ background: colors.gradient, boxShadow: `0 4px 16px ${colors.glow}` }}>
                <i className={config.icon}></i>
                {config.label}
            </span>
        );
    };

    /* ============================================================
       BOUTON ACTION (icône seule)
       ============================================================ */
    const ActionButton = ({ icon, onClick, tooltip, disabled = false, loading = false, color }) => (
        <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
            <Button
                onClick={onClick}
                disabled={disabled || loading}
                className="action-btn-charte"
                style={{
                    '--btn-color': color || COLORS.primary
                }}
            >
                {loading ? <Spinner size="sm" /> : <i className={icon}></i>}
            </Button>
        </OverlayTrigger>
    );

    /* ============================================================
       ÉTAT : CHARGEMENT
       ============================================================ */
    if (loading && !facture) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4 d-flex justify-content-center align-items-center"
                    style={{ minHeight: 'calc(100vh - 140px)', background: '#f8fafc' }}>
                    <div className="text-center">
                        <div className="loading-spinner-charte">
                            <div className="loading-ring-charte"></div>
                            <i className="ri-file-list-3-line loading-icon-charte"></i>
                        </div>
                        <h5 className="fw-bold mt-4 mb-2" style={{ color: COLORS.primary }}>
                            Chargement de la facture
                        </h5>
                        <p className="text-muted small">Récupération des informations en cours...</p>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !facture) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert className="alert-error-charte">
                        <div className="d-flex align-items-center">
                            <div className="alert-error-icon">
                                <i className="ri-error-warning-line"></i>
                            </div>
                            <div className="ms-3 flex-grow-1">
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error || "Facture non trouvée"}</p>
                            </div>
                            <Button onClick={() => navigate(-1)} className="btn-modern-outline">
                                <i className="ri-arrow-left-line me-1"></i>Retour
                            </Button>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const reste = facture.net_a_payer - (facture.montant_regle || 0);
    const pourcentageRegle = facture.net_a_payer > 0 ? Math.round(((facture.montant_regle || 0) / facture.net_a_payer) * 100) : 0;

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-file-list-3-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Détail de la facture</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-barcode-line"></i>
                                        {facture.numero_facture}
                                    </span>
                                    <span className="badge-modern">
                                        <i className="ri-calendar-line"></i>
                                        {formatDate(facture.date_facture)}
                                    </span>
                                    <span className="badge-modern">
                                        <i className="ri-user-line"></i>
                                        {facture.client_nom}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {getStatutBadge(facture.statut)}
                            <div className="d-flex gap-2">
                                <ActionButton icon="ri-arrow-left-line" onClick={() => navigate(-1)} tooltip="Retour" />
                                <ActionButton icon="ri-printer-line" onClick={handlePrint} tooltip="Imprimer" />
                                <ActionButton icon="ri-file-pdf-line" onClick={generatePDF} tooltip="PDF" loading={generatingPDF} disabled={generatingPDF} color={COLORS.danger} />
                            </div>
                        </div>
                    </div>

                    {/* ===== CARTE RÉSUMÉ ===== */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Body className="p-4">
                            <Row className="align-items-center g-3">
                                <Col md={3}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                            <i className="ri-barcode-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">N° Facture</span>
                                            <div className="stat-value-charte" style={{ color: COLORS.primary }}>{facture.numero_facture}</div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={2}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                            <i className="ri-calendar-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">Date</span>
                                            <div className="stat-value-charte">{formatDate(facture.date_facture)}</div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={2}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                            <i className="ri-time-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">Échéance</span>
                                            <div className="stat-value-charte" style={{ color: new Date(facture.date_echeance) < new Date() ? COLORS.danger : '#1e293b' }}>
                                                {formatDate(facture.date_echeance)}
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={2}>
                                    <div className="stat-item-charte">
                                        <div className="stat-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                            <i className="ri-bank-card-line"></i>
                                        </div>
                                        <div>
                                            <span className="stat-label-charte">Total TTC</span>
                                            <div className="stat-value-charte" style={{ color: COLORS.primary, fontWeight: 700 }}>
                                                {formatCurrency(facture.net_a_payer)}
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={3}>
                                    <div className="reste-box-charte" style={{
                                        background: reste > 0 ? `${COLORS.danger}10` : `${COLORS.success}10`,
                                        border: `1px solid ${reste > 0 ? COLORS.danger : COLORS.success}25`
                                    }}>
                                        <span className="small text-muted d-block">Reste à payer</span>
                                        <span className={`fw-bold ${reste > 0 ? 'text-danger' : 'text-success'}`} style={{ fontSize: '1.4rem' }}>
                                            {formatCurrency(reste)}
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== INFOS SOCIÉTÉ & CLIENT ===== */}
                    <Row className="g-3 mb-4">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100 card-hover-charte">
                                <Card.Header className="card-section-header">
                                    <div className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                        <i className="ri-building-line"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Informations société</h6>
                                </Card.Header>
                                <Card.Body>
                                    <h5 className="fw-bold mb-3" style={{ color: COLORS.primary }}>{company.name}</h5>
                                    <p className="info-line-charte"><i className="ri-map-pin-line"></i>{company.address}</p>
                                    <p className="info-line-charte"><i className="ri-phone-line"></i>{company.phone}</p>
                                    <p className="info-line-charte"><i className="ri-mail-line"></i>{company.email}</p>
                                    <p className="info-line-charte"><i className="ri-global-line"></i>{company.site_web}</p>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100 card-hover-charte">
                                <Card.Header className="card-section-header">
                                    <div className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-user-line"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Informations client</h6>
                                </Card.Header>
                                <Card.Body>
                                    <h5 className="fw-bold mb-3">{facture.client_nom}</h5>
                                    <p className="info-line-charte"><i className="ri-barcode-line"></i>Code: <strong>{facture.code_client}</strong></p>
                                    {facture.client_telephone && <p className="info-line-charte"><i className="ri-phone-line"></i>{facture.client_telephone}</p>}
                                    {facture.client_email && <p className="info-line-charte"><i className="ri-mail-line"></i>{facture.client_email}</p>}
                                    {facture.client_adresse && <p className="info-line-charte"><i className="ri-map-pin-line"></i>{facture.client_adresse}</p>}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* ===== TABLEAU PRODUITS ===== */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4">
                        <Card.Header className="card-section-header justify-content-between">
                            <div className="d-flex align-items-center gap-2">
                                <div className="section-icon-charte" style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}>
                                    <i className="ri-shopping-cart-line"></i>
                                </div>
                                <h6 className="mb-0 fw-semibold">Détail des produits</h6>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <span className="badge-modern">
                                    <i className="ri-stack-line"></i>
                                    {facture.lignes?.length || 0} article(s)
                                </span>
                                <span className="badge-modern" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                    <i className="ri-calculator-line"></i>
                                    {facture.lignes?.reduce((acc, l) => acc + (l.quantite || 0), 0)} unités
                                </span>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table className="mb-0 align-middle table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4" style={{ width: '15%' }}>Référence</th>
                                            <th style={{ width: '40%' }}>Désignation</th>
                                            <th className="text-center" style={{ width: '10%' }}>Qté</th>
                                            <th className="text-end" style={{ width: '15%' }}>Prix HT</th>
                                            <th className="text-center" style={{ width: '10%' }}>Remise</th>
                                            <th className="text-end pe-4" style={{ width: '15%' }}>Total HT</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {facture.lignes?.map((ligne, index) => (
                                            <tr key={index}>
                                                <td className="ps-4">
                                                    <span className="reference-badge-charte">
                                                        <i className="ri-barcode-line"></i>{ligne.reference}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{ligne.designation}</div>
                                                    {ligne.description && (
                                                        <small className="text-muted" style={{ fontSize: '0.7rem' }}>{ligne.description}</small>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <span className="quantity-badge-charte">{formatNumber(ligne.quantite)}</span>
                                                </td>
                                                <td className="text-end">{formatCurrency(ligne.prix_unitaire_ht)}</td>
                                                <td className="text-center">
                                                    {ligne.remise ? (
                                                        <span className="remise-badge-charte">
                                                            <i className="ri-discount-percent-line"></i>{ligne.remise}%
                                                        </span>
                                                    ) : <span className="text-muted">-</span>}
                                                </td>
                                                <td className="text-end pe-4 fw-bold" style={{ color: COLORS.primary }}>
                                                    {formatCurrency(ligne.montant_ligne_ht)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="5" className="text-end fw-semibold ps-4">Total HT :</td>
                                            <td className="text-end pe-4 fw-bold" style={{ color: COLORS.primary, fontSize: '1.1rem' }}>{formatCurrency(facture.montant_ht)}</td>
                                        </tr>
                                        {facture.remise_globale > 0 && (
                                            <tr>
                                                <td colSpan="5" className="text-end ps-4 text-success">Remise globale :</td>
                                                <td className="text-end pe-4 fw-semibold text-success">-{formatCurrency(facture.remise_globale)}</td>
                                            </tr>
                                        )}
                                        {facture.montant_tva > 0 && (
                                            <tr>
                                                <td colSpan="5" className="text-end ps-4">TVA ({taxes.tva_taux}%) :</td>
                                                <td className="text-end pe-4 fw-semibold" style={{ color: COLORS.warning }}>{formatCurrency(facture.montant_tva)}</td>
                                            </tr>
                                        )}
                                        {facture.montant_css > 0 && (
                                            <tr>
                                                <td colSpan="5" className="text-end ps-4">CSS ({taxes.css_taux}%) :</td>
                                                <td className="text-end pe-4 fw-semibold" style={{ color: COLORS.info }}>{formatCurrency(facture.montant_css)}</td>
                                            </tr>
                                        )}
                                        <tr className="total-row-charte">
                                            <td colSpan="5" className="text-end fw-bold ps-4" style={{ fontSize: '1.1rem' }}>TOTAL TTC :</td>
                                            <td className="text-end pe-4 fw-bold" style={{ color: COLORS.primary, fontSize: '1.4rem' }}>{formatCurrency(facture.net_a_payer)}</td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== RÉCAP + HISTORIQUE ===== */}
                    <Row className="g-3">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100 card-hover-charte">
                                <Card.Header className="card-section-header">
                                    <div className="section-icon-charte" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-calculator-line"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Récapitulatif financier</h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className="recap-line-charte">
                                        <span>Total HT</span>
                                        <span className="fw-bold">{formatCurrency(facture.montant_ht)}</span>
                                    </div>
                                    {facture.remise_globale > 0 && (
                                        <div className="recap-line-charte text-success">
                                            <span><i className="ri-discount-percent-line me-1"></i>Remise globale</span>
                                            <span className="fw-semibold">-{formatCurrency(facture.remise_globale)}</span>
                                        </div>
                                    )}
                                    {facture.avance_reglement > 0 && (
                                        <div className="recap-line-charte text-info">
                                            <span><i className="ri-money-euro-circle-line me-1"></i>Avance / Acompte</span>
                                            <span className="fw-semibold">-{formatCurrency(facture.avance_reglement)}</span>
                                        </div>
                                    )}
                                    {taxes.tva_active && facture.montant_tva > 0 && (
                                        <div className="recap-line-charte">
                                            <span>TVA ({taxes.tva_taux}%)</span>
                                            <span className="fw-semibold" style={{ color: COLORS.warning }}>+{formatCurrency(facture.montant_tva)}</span>
                                        </div>
                                    )}
                                    {taxes.css_active && facture.montant_css > 0 && (
                                        <div className="recap-line-charte">
                                            <span>CSS ({taxes.css_taux}%)</span>
                                            <span className="fw-semibold" style={{ color: COLORS.info }}>+{formatCurrency(facture.montant_css)}</span>
                                        </div>
                                    )}

                                    <div className="recap-total-charte">
                                        <span className="fw-bold">TOTAL TTC</span>
                                        <span className="fw-bold" style={{ color: COLORS.primary, fontSize: '1.5rem' }}>
                                            {formatCurrency(facture.net_a_payer)}
                                        </span>
                                    </div>

                                    <div className="recap-line-charte mt-3">
                                        <span><i className="ri-bank-card-line me-1"></i>Total réglé</span>
                                        <span className="fw-bold text-success">{formatCurrency(facture.montant_regle || 0)}</span>
                                    </div>
                                    <div className="recap-line-charte ps-3">
                                        <span className="small text-muted"><i className="ri-money-euro-circle-line me-1"></i>Dont avance</span>
                                        <span className="small text-info">{formatCurrency(facture.avance_reglement || 0)}</span>
                                    </div>
                                    {((facture.montant_regle || 0) - (facture.avance_reglement || 0)) > 0 && (
                                        <div className="recap-line-charte ps-3">
                                            <span className="small text-muted"><i className="ri-bank-card-line me-1"></i>Dont paiements</span>
                                            <span className="small text-success">{formatCurrency((facture.montant_regle || 0) - (facture.avance_reglement || 0))}</span>
                                        </div>
                                    )}

                                    <div className="recap-reste-charte">
                                        <span className="fw-semibold">Reste à payer</span>
                                        <span className={`fw-bold ${reste > 0 ? 'text-danger' : 'text-success'}`} style={{ fontSize: '1.8rem' }}>
                                            {formatCurrency(reste)}
                                        </span>
                                    </div>

                                    {facture.net_a_payer > 0 && (
                                        <div className="progress-wrapper-charte">
                                            <div className="d-flex justify-content-between mb-1">
                                                <span className="small text-muted">Taux de règlement</span>
                                                <span className="small fw-semibold" style={{ color: COLORS.primary }}>{pourcentageRegle}%</span>
                                            </div>
                                            <div className="progress-charte">
                                                <div className="progress-bar-charte" style={{ width: `${Math.min(pourcentageRegle, 100)}%` }}></div>
                                            </div>
                                            <div className="d-flex justify-content-between mt-1">
                                                <small className="text-muted">Payé: {formatCurrency(facture.montant_regle || 0)}</small>
                                                <small className="text-muted">Total: {formatCurrency(facture.net_a_payer)}</small>
                                            </div>
                                        </div>
                                    )}

                                    <div className="d-grid gap-2 mt-4">
                                        {facture.statut !== 'payee' && facture.statut !== 'annulee' && (
                                            <Button
                                                onClick={() => { setPaiement({...paiement, montant: reste}); setShowPaiementModal(true); }}
                                                disabled={reste <= 0}
                                                className="btn-modern-success py-2"
                                            >
                                                <i className="ri-bank-card-line me-2"></i>
                                                Encaisser {reste > 0 && `(${formatCurrency(reste)})`}
                                            </Button>
                                        )}
                                        <Button
                                            onClick={() => setShowAvoirModal(true)}
                                            className="btn-modern-warning py-2"
                                        >
                                            <i className="ri-file-copy-line me-2"></i>Créer un avoir
                                        </Button>
                                        {facture.statut === 'payee' && (
                                            <div className="text-center py-2 rounded-3" style={{ background: `${COLORS.success}15`, color: COLORS.success, fontSize: '0.85rem', fontWeight: '500' }}>
                                                <i className="ri-checkbox-circle-line me-1"></i>Facture entièrement réglée
                                            </div>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100 card-hover-charte">
                                <Card.Header className="card-section-header">
                                    <div className="section-icon-charte" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                        <i className="ri-history-line"></i>
                                    </div>
                                    <h6 className="mb-0 fw-semibold">Historique des paiements</h6>
                                    {facture.paiements?.length > 0 && (
                                        <span className="badge-count-charte ms-auto" style={{ background: COLORS.info }}>{facture.paiements.length}</span>
                                    )}
                                </Card.Header>
                                <Card.Body>
                                    {facture.paiements?.length > 0 ? (
                                        <div className="d-flex flex-column gap-3">
                                            {facture.paiements.map((p, index) => {
                                                const mode = MODE_REGLEMENT.find(m => m.value === p.mode_reglement);
                                                return (
                                                    <div key={index} className="paiement-item-charte">
                                                        <div className="d-flex justify-content-between align-items-start">
                                                            <div className="d-flex align-items-center gap-3">
                                                                <div className="paiement-icon-charte" style={{
                                                                    background: p.statut === 'valide' ? `${COLORS.success}15` : `${COLORS.warning}15`,
                                                                    color: p.statut === 'valide' ? COLORS.success : COLORS.warning
                                                                }}>
                                                                    <i className={mode?.icon || 'ri-bank-line'}></i>
                                                                </div>
                                                                <div>
                                                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                        <h6 className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>{mode?.label || p.mode_reglement}</h6>
                                                                        <span className="badge-status-charte" style={{
                                                                            background: p.statut === 'valide' ? `${COLORS.success}15` : `${COLORS.warning}15`,
                                                                            color: p.statut === 'valide' ? COLORS.success : COLORS.warning
                                                                        }}>
                                                                            <i className={`${p.statut === 'valide' ? 'ri-check-line' : 'ri-time-line'} me-1`}></i>
                                                                            {p.statut === 'valide' ? 'Validé' : 'En attente'}
                                                                        </span>
                                                                    </div>
                                                                    <div className="d-flex align-items-center gap-3 mt-1 flex-wrap">
                                                                        <small className="text-muted"><i className="ri-calendar-line me-1"></i>{formatDate(p.date_reglement)}</small>
                                                                        {p.reference && <small className="text-muted"><i className="ri-file-text-line me-1"></i>Réf: {p.reference}</small>}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="text-end">
                                                                <div className="fw-bold text-success" style={{ fontSize: '1.1rem' }}>{formatCurrency(p.montant)}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="empty-state-charte">
                                            <div className="empty-state-icon-charte">
                                                <i className="ri-bank-card-line"></i>
                                            </div>
                                            <h6 className="fw-semibold mb-2">Aucun paiement enregistré</h6>
                                            <p className="text-muted small mb-0">Les paiements effectués apparaîtront ici</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {facture.notes && (
                        <Card className="border-0 shadow-sm rounded-3 mt-4">
                            <Card.Header className="card-section-header">
                                <div className="section-icon-charte" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                    <i className="ri-file-text-line"></i>
                                </div>
                                <h6 className="mb-0 fw-semibold">Notes</h6>
                            </Card.Header>
                            <Card.Body>
                                <p className="mb-0" style={{ color: '#475569' }}>{facture.notes}</p>
                            </Card.Body>
                        </Card>
                    )}
                </div>
                <Footer />
            </div>

            {/* ===== MODAL PAIEMENT ===== */}
            <Modal show={showPaiementModal} onHide={() => setShowPaiementModal(false)} centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.success}, #059669)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon">
                            <i className="ri-bank-card-line"></i>
                        </div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Enregistrer un encaissement</Modal.Title>
                            <p className="text-white-50 mb-0 small">Saisissez les informations du paiement</p>
                        </div>
                    </div>
                </Modal.Header>
                <Form onSubmit={handlePaiementSubmit}>
                    <Modal.Body className="p-4">
                        <div className="modal-info-box-charte mb-4">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="text-muted d-block">Facture</small>
                                    <strong>{facture.numero_facture}</strong>
                                    <small className="text-muted d-block">{facture.client_nom}</small>
                                </div>
                                <div className="text-end">
                                    <small className="text-muted d-block">Reste à payer</small>
                                    <strong className="text-danger" style={{ fontSize: '1.3rem' }}>{formatCurrency(reste)}</strong>
                                </div>
                            </div>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label className="form-label-modern">Date de paiement</Form.Label>
                            <InputGroup className="input-modern">
                                <InputGroup.Text><i className="ri-calendar-line"></i></InputGroup.Text>
                                <Form.Control
                                    type="date"
                                    value={paiement.date_reglement}
                                    onChange={(e) => setPaiement({...paiement, date_reglement: e.target.value})}
                                    required
                                />
                            </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="form-label-modern">Montant</Form.Label>
                            <InputGroup className="input-modern">
                                <InputGroup.Text><i className="ri-money-euro-circle-line"></i></InputGroup.Text>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    max={reste}
                                    value={paiement.montant}
                                    onChange={(e) => setPaiement({...paiement, montant: e.target.value})}
                                    required
                                />
                                <InputGroup.Text className="bg-light">FCFA</InputGroup.Text>
                            </InputGroup>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="form-label-modern">Mode de règlement</Form.Label>
                            <Form.Select
                                value={paiement.mode_reglement}
                                onChange={(e) => setPaiement({...paiement, mode_reglement: e.target.value})}
                                required
                                className="input-select-modern"
                            >
                                {MODE_REGLEMENT.map(mode => (
                                    <option key={mode.value} value={mode.value}>{mode.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-0">
                            <Form.Label className="form-label-modern">Référence (optionnel)</Form.Label>
                            <InputGroup className="input-modern">
                                <InputGroup.Text><i className="ri-file-text-line"></i></InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="N° chèque, référence virement..."
                                    value={paiement.reference}
                                    onChange={(e) => setPaiement({...paiement, reference: e.target.value})}
                                />
                            </InputGroup>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                        <Button onClick={() => setShowPaiementModal(false)} className="btn-modern-outline">Annuler</Button>
                        <Button type="submit" disabled={saving} className="btn-modern-success px-4">
                            {saving ? <><Spinner size="sm" className="me-2" />Enregistrement...</> : <><i className="ri-check-line me-2"></i>Enregistrer</>}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* ===== MODAL AVOIR ===== */}
            <Modal show={showAvoirModal} onHide={() => setShowAvoirModal(false)} size="lg" centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.warning}, #d97706)` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon">
                            <i className="ri-file-copy-line"></i>
                        </div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Créer un avoir</Modal.Title>
                            <p className="text-white-50 mb-0 small">Sélectionnez les lignes à avoir</p>
                        </div>
                    </div>
                </Modal.Header>
                <Form onSubmit={handleAvoirSubmit}>
                    <Modal.Body className="p-4">
                        <Alert className="alert-info-charte mb-4">
                            <i className="ri-information-line me-2"></i>
                            L'avoir sera émis pour les lignes sélectionnées ci-dessous.
                        </Alert>

                        <Form.Group className="mb-4">
                            <Form.Label className="form-label-modern">Motif de l'avoir</Form.Label>
                            <InputGroup className="input-modern">
                                <InputGroup.Text><i className="ri-chat-1-line"></i></InputGroup.Text>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={avoir.motif}
                                    onChange={(e) => setAvoir({...avoir, motif: e.target.value})}
                                    placeholder="Raison de l'avoir..."
                                    required
                                />
                            </InputGroup>
                        </Form.Group>

                        <h6 className="mb-3 fw-semibold">Sélectionnez les lignes à avoir :</h6>
                        <div className="table-responsive">
                            <Table className="align-middle table-charte">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }}>
                                            <Form.Check
                                                type="checkbox"
                                                checked={avoir.lignes.every(l => l.selected)}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    setAvoir({...avoir, lignes: avoir.lignes.map(l => ({...l, selected: checked}))});
                                                }}
                                            />
                                        </th>
                                        <th>Produit</th>
                                        <th className="text-center">Quantité</th>
                                        <th className="text-end">Prix HT</th>
                                        <th className="text-end">Total HT</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {avoir.lignes.map((ligne, index) => (
                                        <tr key={index}>
                                            <td>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={ligne.selected}
                                                    onChange={(e) => {
                                                        const newLignes = [...avoir.lignes];
                                                        newLignes[index].selected = e.target.checked;
                                                        setAvoir({...avoir, lignes: newLignes});
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                <div className="fw-semibold" style={{ fontSize: '0.85rem' }}>{ligne.designation}</div>
                                                <small className="text-muted">{ligne.reference}</small>
                                            </td>
                                            <td className="text-center">
                                                <Form.Control
                                                    type="number"
                                                    min="1"
                                                    max={ligne.quantite}
                                                    size="sm"
                                                    style={{ width: '80px', margin: '0 auto', textAlign: 'center' }}
                                                    value={ligne.quantite_avoir}
                                                    disabled={!ligne.selected}
                                                    onChange={(e) => {
                                                        const newLignes = [...avoir.lignes];
                                                        newLignes[index].quantite_avoir = parseInt(e.target.value) || 1;
                                                        setAvoir({...avoir, lignes: newLignes});
                                                    }}
                                                    className="input-table-charte"
                                                />
                                            </td>
                                            <td className="text-end">{formatCurrency(ligne.prix_unitaire_ht)}</td>
                                            <td className="text-end fw-semibold">
                                                {ligne.selected ? formatCurrency(ligne.prix_unitaire_ht * ligne.quantite_avoir) : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                        <Button onClick={() => setShowAvoirModal(false)} className="btn-modern-outline">Annuler</Button>
                        <Button type="submit" disabled={saving} className="btn-modern-warning px-4">
                            {saving ? <><Spinner size="sm" className="me-2" />Création...</> : <><i className="ri-check-line me-2"></i>Créer</>}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white;
                    padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .badge-count-charte {
                    min-width: 24px; height: 24px; border-radius: 50px;
                    display: inline-flex; align-items: center; justify-content: center;
                    color: white; font-size: 0.7rem; font-weight: 600; padding: 0 0.5rem;
                }
                .status-badge-charte {
                    color: white; padding: 0.4rem 1.2rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 0.5rem;
                }

                /* ===== BOUTON ACTION ===== */
                .action-btn-charte {
                    width: 42px; height: 42px; border-radius: 12px;
                    display: inline-flex; align-items: center; justify-content: center;
                    border: 1.5px solid #e2e8f0; background: white;
                    color: #64748b; font-size: 1.1rem;
                    transition: all 0.2s ease;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color);
                    color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== CARTE STATS ===== */
                .stat-item-charte { display: flex; align-items: center; gap: 0.75rem; }
                .stat-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .stat-label-charte {
                    display: block; font-size: 0.7rem; font-weight: 500;
                    text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8;
                }
                .stat-value-charte { font-size: 1.05rem; font-weight: 600; color: #1e293b; }
                .reste-box-charte {
                    padding: 0.75rem 1.25rem; border-radius: 12px;
                    text-align: right; display: inline-block;
                }

                /* ===== SECTIONS DE CARTE ===== */
                .card-section-header {
                    background: transparent;
                    border: none;
                    padding: 1.25rem 1.5rem 0.75rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }
                .section-icon-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.1rem;
                }
                .card-hover-charte { transition: all 0.3s ease; }
                .card-hover-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 32px rgba(0,0,0,0.08) !important;
                }

                /* ===== INFO LINE ===== */
                .info-line-charte {
                    display: flex; align-items: center; gap: 0.6rem;
                    font-size: 0.85rem; color: #475569;
                    margin-bottom: 0.6rem;
                }
                .info-line-charte i { color: var(--secondary); font-size: 1rem; }

                /* ===== TABLE CHARTE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.85rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-charte tfoot td {
                    background: #f8fafc;
                    padding: 0.85rem 0.75rem;
                    border-top: 2px solid #e2e8f0;
                }
                .table-charte tfoot tr.total-row-charte td {
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    border-top: 2px solid var(--primary);
                }
                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .quantity-badge-charte {
                    display: inline-flex; align-items: center; justify-content: center;
                    background: #e2e8f0; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.8rem;
                    font-weight: 600; min-width: 32px;
                }
                .remise-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #fef3c7; color: #92400e;
                    padding: 0.15rem 0.5rem; border-radius: 6px;
                    font-size: 0.7rem; font-weight: 500;
                }
                .input-table-charte {
                    border: 1.5px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 0.85rem;
                    padding: 0.3rem 0.5rem;
                }
                .input-table-charte:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }

                /* ===== RÉCAP ===== */
                .recap-line-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 0.5rem 0;
                    border-bottom: 1px dashed #e2e8f0;
                    font-size: 0.85rem;
                    color: #475569;
                }
                .recap-line-charte:last-of-type { border-bottom: none; }
                .recap-total-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 0.85rem 1rem;
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    border-radius: 12px;
                    margin-top: 0.5rem;
                    border: 1px solid #e2e8f0;
                }
                .recap-reste-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem;
                    background: #f8fafc;
                    border-radius: 12px;
                    margin-top: 1rem;
                }
                .progress-wrapper-charte { margin-top: 1rem; }
                .progress-charte {
                    height: 10px; border-radius: 6px;
                    background: #e2e8f0; overflow: hidden;
                    box-shadow: inset 0 1px 3px rgba(0,0,0,0.05);
                }
                .progress-bar-charte {
                    height: 100%; border-radius: 6px;
                    background: linear-gradient(90deg, var(--success), #059669);
                    transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
                    box-shadow: 0 2px 8px rgba(16,185,129,0.3);
                }

                /* ===== PAIEMENT ITEM ===== */
                .paiement-item-charte {
                    padding: 1rem; background: white;
                    border-radius: 14px;
                    border: 1px solid #e2e8f0;
                    transition: all 0.3s ease;
                }
                .paiement-item-charte:hover {
                    transform: translateX(5px);
                    border-color: var(--secondary);
                    box-shadow: 0 8px 24px rgba(33,172,205,0.12);
                }
                .paiement-icon-charte {
                    width: 44px; height: 44px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.2rem; flex-shrink: 0;
                }
                .badge-status-charte {
                    display: inline-flex; align-items: center;
                    padding: 0.15rem 0.55rem; border-radius: 50px;
                    font-size: 0.65rem; font-weight: 600;
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 1rem; }
                .empty-state-icon-charte {
                    width: 80px; height: 80px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 2rem; color: #94a3b8;
                }

                /* ===== BOUTONS MODERNES ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-success {
                    background: linear-gradient(135deg, var(--success), #059669);
                    border: none; color: white; padding: 0.6rem 2rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.9rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(16,185,129,0.3);
                    display: inline-flex; align-items: center; justify-content: center;
                }
                .btn-modern-success:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(16,185,129,0.4);
                    color: white;
                }
                .btn-modern-warning {
                    background: linear-gradient(135deg, var(--warning), #d97706);
                    border: none; color: white; padding: 0.6rem 2rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.9rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(245,158,11,0.3);
                    display: inline-flex; align-items: center; justify-content: center;
                }
                .btn-modern-warning:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(245,158,11,0.4);
                    color: white;
                }

                /* ===== ALERTES ===== */
                .alert-error-charte {
                    border: none; background: #fef2f2;
                    border-left: 4px solid var(--danger);
                    border-radius: 16px; padding: 1rem 1.5rem;
                    box-shadow: 0 8px 32px rgba(239,68,68,0.08);
                }
                .alert-error-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(239,68,68,0.1);
                    display: flex; align-items: center; justify-content: center;
                    color: var(--danger); font-size: 1.5rem;
                }
                .alert-info-charte {
                    border: none; background: #eff6ff;
                    border-left: 4px solid var(--info);
                    border-radius: 12px; padding: 1rem 1.25rem;
                    font-size: 0.85rem; color: #1e40af;
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px;
                    overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte {
                    padding: 1.25rem 1.5rem;
                    border: none;
                }
                .modal-header-charte .btn-close {
                    filter: brightness(0) invert(1);
                    opacity: 0.8;
                }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }
                .modal-info-box-charte {
                    padding: 1rem 1.25rem;
                    background: #f8fafc;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                }

                /* ===== INPUTS MODERNES ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .input-modern .input-group-text.bg-light {
                    background: #f1f5f9 !important;
                    border-left: none;
                    border-radius: 0 10px 10px 0;
                    color: #64748b; font-weight: 600; font-size: 0.85rem;
                }
                .input-select-modern {
                    border: 2px solid #e2e8f0; border-radius: 10px;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-select-modern:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }

                /* ===== LOADING ===== */
                .loading-spinner-charte {
                    position: relative; width: 80px; height: 80px; margin: 0 auto;
                }
                .loading-ring-charte {
                    position: absolute; inset: 0; border-radius: 50%;
                    border: 4px solid #e2e8f0;
                    border-top-color: var(--primary);
                    animation: spin 1s linear infinite;
                }
                .loading-icon-charte {
                    position: absolute; top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 28px; color: var(--primary);
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999;
                    animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress {
                    height: 3px; width: 100%;
                    animation: shrink linear forwards;
                }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .stat-item-charte { margin-bottom: 0.75rem; }
                    .reste-box-charte { width: 100%; text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                    .recap-reste-charte span:last-child { font-size: 1.4rem !important; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}